import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaArrowLeft, FaLock } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { Subtitle, BodyText, Button, Input, Title } from '../../components/ui';
import Layout from '../../components/Layout';
import { resetPassword } from '../../api/services/customerService';
import { setShowLoading } from '../../redux/systemInfo';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 90vh;
  padding: 20px;
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  margin-top: 20px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
`;

const FormContainerTitle = styled.div`
  width: 100%;
  padding: 20px 0;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.subBackground};
`;

const FormContainerBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5% 10%;
`;

const InputContainer = styled.div`
  width: 100%;
  padding: 5px 5px;
  border: 1px solid #ccc;
  gap: 10px;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primaryBlue};
  cursor: pointer;
  font-size: 16px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
  position: absolute;
  top: 30px;
  left: 50px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState({
    isShow: false,
    msg: '',
  });
  const [confirmPasswordError, setConfirmPasswordError] = useState({
    isShow: false,
    msg: '',
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Get token and email from URL parameters
    const tokenFromUrl = searchParams.get('token');
    const emailFromUrl = searchParams.get('email');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      // If no token, redirect to forgot password page
      navigate('/forgot-password');
    }
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
  }, [searchParams, navigate]);

  const handleResetPassword = async () => {
    // Reset error states
    setPasswordError({ isShow: false, msg: '' });
    setConfirmPasswordError({ isShow: false, msg: '' });

    // Validate password
    if (!newPassword) {
      setPasswordError({ isShow: true, msg: 'Password is required' });
      return;
    }

    // Validate confirm password
    if (!confirmPassword) {
      setConfirmPasswordError({
        isShow: true,
        msg: 'Please confirm your password',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError({ isShow: true, msg: 'Passwords do not match' });
      return;
    }

    try {
      dispatch(setShowLoading({ showLoading: true }));
      const result = await resetPassword(token, newPassword, email);
      dispatch(setShowLoading({ showLoading: false }));

      if (result.is_success) {
        setIsSuccess(true);
        // Navigate to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setPasswordError({
          isShow: true,
          msg: result.message || 'Failed to reset password',
        });
      }
    } catch (error) {
      dispatch(setShowLoading({ showLoading: false }));
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'An error occurred. Please try again.';
      setPasswordError({
        isShow: true,
        msg: errorMessage,
      });
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Layout>
      <Container>
        <BackButton onClick={handleBackToLogin}>
          <FaArrowLeft />
          Back to Login
        </BackButton>

        <Title
          style={{
            margin: '20px 0 30px 0',
            textAlign: 'center',
          }}
        >
          Reset Password
        </Title>

        <Wrapper>
          <FormContainer>
            <FormContainerTitle>
              <Subtitle align='center'>Create New Password</Subtitle>
            </FormContainerTitle>
            <FormContainerBody>
              <IconContainer>
                <FaLock size={'40px'} color='#223a7a' />
              </IconContainer>

              {!isSuccess ? (
                <>
                  <BodyText
                    weight={'normal'}
                    align={'center'}
                    margin='0 0 20px 0'
                    style={{ lineHeight: '1.5' }}
                  >
                    Please enter your new password below. Make sure it&apos;s
                    secure and easy to remember.
                  </BodyText>

                  <BodyText weight={'bold'} align={'left'} margin='0 0 10px 0'>
                    New Password:
                  </BodyText>
                  <InputContainer>
                    <Input
                      type='password'
                      placeholder='Enter your new password'
                      value={newPassword}
                      onChange={setNewPassword}
                    />
                  </InputContainer>
                  {passwordError.isShow ? (
                    <BodyText color={'red'}>{passwordError.msg}</BodyText>
                  ) : null}

                  <BodyText
                    weight={'bold'}
                    align={'left'}
                    margin='20px 0 10px 0'
                    style={{ color: '#223a7a' }}
                  >
                    Confirm Password:
                  </BodyText>
                  <InputContainer>
                    <Input
                      type='password'
                      placeholder='Confirm your new password'
                      value={confirmPassword}
                      onChange={setConfirmPassword}
                    />
                  </InputContainer>
                  {confirmPasswordError.isShow ? (
                    <BodyText color={'red'}>
                      {confirmPasswordError.msg}
                    </BodyText>
                  ) : null}

                  <Button
                    style={{
                      alignSelf: 'center',
                      width: '100%',
                      padding: '15px',
                      marginTop: '20px',
                    }}
                    onClick={handleResetPassword}
                  >
                    <Subtitle color='white'>Reset Password</Subtitle>
                  </Button>
                </>
              ) : (
                <>
                  <BodyText
                    weight={'normal'}
                    align={'center'}
                    margin='0 0 20px 0'
                    style={{ lineHeight: '1.5', color: 'green' }}
                  >
                    Your password has been successfully reset! You will be
                    redirected to the login page in a few seconds.
                  </BodyText>

                  <Button
                    style={{
                      alignSelf: 'center',
                      width: '100%',
                      padding: '15px',
                      marginTop: '20px',
                    }}
                    onClick={() => navigate('/login')}
                  >
                    <Subtitle color='white'>Go to Login</Subtitle>
                  </Button>
                </>
              )}

              <BodyText
                weight={'normal'}
                align={'center'}
                margin='20px 0 0 0'
                style={{ fontSize: '14px', color: '#666' }}
              >
                Remember your password?{' '}
                <span
                  style={{
                    color: '#223a7a',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  onClick={handleBackToLogin}
                >
                  Sign in here
                </span>
              </BodyText>
            </FormContainerBody>
          </FormContainer>
        </Wrapper>
      </Container>
    </Layout>
  );
};

export default ResetPasswordPage;
